import {
  Checkbox,
  Col,
  DatePicker,
  Divider,
  Flex,
  Form,
  Input,
  Row
} from 'antd'
import { AnyObject } from 'antd/es/_util/type'
import { FormInstance } from 'antd/lib'
import dayjs, { Dayjs } from 'dayjs'
import { useEffect } from 'react'

import { useTreatmentServiceGetTreatmentPlanEndpoint } from 'src/api/queries'
import {
  ClassifierCategory,
  ClassifierType,
  GetPersonByIdResponse,
  Permissions,
  TreatmentPlanStatus
} from 'src/api/requests'
import { requiredRules } from 'src/app-constants'
import {
  Text,
  ClassifierInfiniteSelect,
  TreatmentTypeRegimenInfiniteSelect,
  TreatmentContingentTypeInfiniteSelect
} from 'src/components'
import { EXISTING_TREATMENT_PLAN } from 'src/features/persons/utils'
import { useClassifierId, useUser } from 'src/hooks'
import { dayMonthYearBackSlash } from 'src/libs'
import { useLocales } from 'src/locales'

import { useStyles } from '../styles'

import { TreatmentsConsumablesTables } from './TreatmentsConsumablesTables'

type Props = {
  form: FormInstance
  person?: GetPersonByIdResponse
}

export const Treatment = ({ form, person }: Props) => {
  const { t } = useLocales()
  const { hasPermission } = useUser()
  const { styles } = useStyles()

  const treatmentTypes = useClassifierId(
    ClassifierType.TREATMENT_TYPE,
    ClassifierCategory.TREATMENT
  )

  const selectedTypeId = Form.useWatch('treatment_type', form)
  const treatmentPlanId = form.getFieldValue(EXISTING_TREATMENT_PLAN)

  const hasPregnancy = Form.useWatch('hasPregnancy', form)

  const treatmentStartDate = Form.useWatch('treatment_start_date', form)

  const disabledEndDate = (current: Dayjs) => {
    return !dayjs(current).isAfter(treatmentStartDate, 'day')
  }

  const { data: treatmentDetails } =
    useTreatmentServiceGetTreatmentPlanEndpoint({
      id: treatmentPlanId
    })

  const isDisabled =
    treatmentDetails?.status === TreatmentPlanStatus.CANCELLED ||
    treatmentDetails?.status === TreatmentPlanStatus.COMPLETED

  const isPatient = !!person?.simeCode || !!person?.tarvCode

  useEffect(() => {
    const medicine = form?.getFieldValue('medicine') || []
    const paths =
      medicine?.flatMap((_: AnyObject, i: number) => [
        ['medicine', i, 'startDate'],
        ['medicine', i, 'endDate']
      ]) ?? []

    if (paths?.length) {
      form?.validateFields(paths).catch(() => {})
    }
  }, [
    form,
    form?.getFieldValue('treatment_start_date'),
    form?.getFieldValue('treatment_end_date')
  ])

  useEffect(() => {
    if (isPatient) {
      form?.setFieldsValue({
        simeCode: person?.simeCode,
        tarvCode: person?.tarvCode
      })
    }
  }, [isPatient])

  return (
    <>
      {(hasPregnancy || isPatient) && (
        <>
          <Flex gap={16} className={styles.treatmentContent}>
            <Form.Item shouldUpdate>
              <Row gutter={[24, 16]}>
                {hasPregnancy && (
                  <Col span={24}>
                    <Form.Item name="isUrgent" valuePropName="checked">
                      <Checkbox>{t('treatments.urgent_treatment')}</Checkbox>
                    </Form.Item>
                  </Col>
                )}

                {isPatient && (
                  <>
                    <Col span={12}>
                      <Form.Item
                        name="simeCode"
                        label={t(`common.sime_code`)}
                        rules={requiredRules}
                      >
                        <Input
                          className="full-width"
                          disabled={isDisabled || !!treatmentDetails?.id}
                          placeholder={t(`common.sime_code`)}
                        />
                      </Form.Item>
                    </Col>

                    <Col span={12}>
                      <Form.Item name="tarvCode" label={t(`common.tarv_code`)}>
                        <Input
                          className="full-width"
                          disabled={isDisabled}
                          placeholder={t(`common.tarv_code`)}
                        />
                      </Form.Item>
                    </Col>
                  </>
                )}
              </Row>
            </Form.Item>
          </Flex>
          <Divider />
        </>
      )}
      <Flex gap={16} className={styles.treatmentContent} wrap>
        <Form.Item
          name="treatment_type"
          label={t('treatments.treatment_type')}
          rules={requiredRules}
        >
          <ClassifierInfiniteSelect
            id={treatmentTypes}
            disabled={isDisabled || !!treatmentDetails?.id}
            placeholder={t('treatments.treatment_type')}
          />
        </Form.Item>

        <Form.Item
          name="preventionTreatmentContingentTypeId"
          label={t('investigations.treatment_reason')}
        >
          <TreatmentContingentTypeInfiniteSelect
            typesId={selectedTypeId}
            disabled={!selectedTypeId || isDisabled || !!treatmentDetails?.id}
          />
        </Form.Item>

        <Form.Item
          name="treatmentTypeRegimenId"
          label={t('treatments.regiment_type')}
          rules={requiredRules}
        >
          <TreatmentTypeRegimenInfiniteSelect
            typesId={[selectedTypeId]}
            disabled={!selectedTypeId || isDisabled || !!treatmentDetails?.id}
          />
        </Form.Item>

        <Form.Item
          name="treatment_start_date"
          label={t('persons.treatment.start_date')}
          rules={[...(requiredRules ?? [])]}
        >
          <DatePicker
            className="full-width"
            placeholder={t('persons.treatment.start_date')}
            format={dayMonthYearBackSlash}
            disabled={
              !!treatmentDetails?.id ||
              isDisabled ||
              !!treatmentDetails?.endDate
            }
          />
        </Form.Item>

        <Form.Item
          name="treatment_end_date"
          label={t('persons.treatment.end_date')}
          rules={[
            ...(requiredRules ?? []),
            {
              validator: (_, value) => {
                if (!value) return Promise.resolve()
                if (disabledEndDate(value)) {
                  return Promise.reject(
                    new Error(t('persons.treatment.end_date_invalid'))
                  )
                }
                return Promise.resolve()
              }
            }
          ]}
        >
          <DatePicker
            className="full-width"
            placeholder={t('persons.treatment.end_date')}
            format={dayMonthYearBackSlash}
            disabled={!treatmentStartDate || isDisabled}
            disabledDate={disabledEndDate}
          />
        </Form.Item>
      </Flex>

      {hasPermission(Permissions.TREATMENT_TREATMENT_PLAN_VIEW_PRESCRIBED) && (
        <>
          <Divider />
          <Flex key={1} vertical gap={24} className={styles.treatmentContent}>
            <Text size="xl">{t('persons.treatment.treatment_prescribed')}</Text>

            <Form.Item name="medicine" rules={requiredRules}>
              <Form.List name="medicine">
                {(fields, { remove }) => (
                  <TreatmentsConsumablesTables
                    fields={fields}
                    remove={remove}
                    form={form}
                    isDisabled={isDisabled}
                    data={treatmentDetails}
                  />
                )}
              </Form.List>
            </Form.Item>
          </Flex>
        </>
      )}
    </>
  )
}
